<template>
  <div :class="rootClasses">
    <div :class="['real-wish', { 'vertically-centered': verticallyCenterContent }]">
      <!-- This out-of-bounds (oob) h3 acts as a title for posts that don't have one, for consistency. -->
      <h3 class="accessibility oob" tabindex="0">{{ headline }}</h3>
      <surface-post-author v-if="xAuthor" :post="post" :user="user" class="author" />
      <div v-if="isWishContentScrollable" class="wish-content">
        <section v-if="xWords" :class="['words', { 'vertically-centered': verticallyCenterContent }]">
          <surface-post-words-edit
            :post="post"
            @text-change="
              changePostText({
                postCid: post.cid,
                attributes: $event,
              })
            "
          />
        </section>
        <surface-post-attachment v-if="xAttachment" :post="post" :is-being-edited="true" />
      </div>
      <template v-else>
        <section v-if="xWords" :class="['words', { 'vertically-centered': verticallyCenterContent }]">
          <surface-post-words-edit
            :post="post"
            @text-change="
              changePostText({
                postCid: post.cid,
                attributes: $event,
              })
            "
          />
        </section>
        <surface-post-attachment v-if="xAttachment" :post="post" :is-being-edited="true" />
      </template>
      <surface-post-moderation
        v-if="xModeration"
        :post="post"
        :can-i-moderate="canIModerate"
        :is-being-edited="true"
        @approve="approvePost({ postCid: post.cid })"
        @delete="confirmDelete"
      />
      <section v-if="xComments || xReactions" class="stalker-bar">
        <surface-post-comments
          v-if="xComments && !xPostCommentsInPanel"
          :post="post"
          :user="user"
          :comment-ids="postCommentIds"
          :comments="wallComments"
          :is-commentable="isCommentable"
        />
      </section>
    </div>
    <surface-post-actions
      :x-author="xAuthor"
      :post="post"
      :attachment-luminance="attachmentLuminance"
      :color-scheme="colorScheme"
      :is-editable-by-current-user="isEditableByCurrentUser"
      :is-being-edited="true"
      @delete="confirmDelete"
    />
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { mapState, mapActions, mapGetters } from 'vuex'
import SurfacePostAuthor from 'vuecomponents/surface_post_author'
import SurfacePostWordsEdit from 'vuecomponents/surface_post_words_edit'
import SurfacePostAttachment from 'vuecomponents/surface_post_attachment'
import SurfacePostModeration from 'vuecomponents/surface_post_moderation'
import SurfacePostComments from 'vuecomponents/surface_post_comments'
import SurfacePostActions from 'vuecomponents/surface_post_actions'
import IntlMixin from 'vuemixins/intl_mixin'
import OutsideEventsMixin from 'vuemixins/outside_events_mixin'
import { PostConfiguration } from 'vuexstore/helpers/post'
import { isUrl } from 'bits/url'

export default {
  components: {
    SurfacePostAuthor,
    SurfacePostWordsEdit,
    SurfacePostAttachment,
    SurfacePostComments,
    SurfacePostActions,
    SurfacePostModeration,
  },
  mixins: [IntlMixin, OutsideEventsMixin],
  props: {
    index: { type: Number, default: null },
    post: { type: Object, default: () => ({}) },
    edit: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false },
    fitHeightToContainer: { type: Boolean, default: false },
    verticallyCenterContent: { type: Boolean, default: false },
  },
  computed: {
    isPublished() {
      return this.post.published
    },
    subject() {
      return this.post.subject
    },
    body() {
      return this.post.body
    },
    sectionId() {
      return this.post.wall_section_id
    },
    headline() {
      return this.post.headline
    },
    ...mapState({
      user: 'user',
    }),
    ...mapState('comment', {
      commentBeingEditedId: 'commentBeingEditedId',
      wallComments: 'commentEntities',
    }),
    ...mapGetters('comment', {
      wallCommentIdsByPost: 'commentIdsByPost',
    }),
    ...mapGetters('attachment', ['linkDisplayAttributesForUrl']),
    ...mapGetters('post', ['postConfiguration']),
    ...mapGetters([
      'xAuthor',
      'isBackchannel',
      'isTimeline',
      'isCommentable',
      'isReactable',
      'canIModerate',
      'canIWrite',
      'format',
      'xPostCommentsInPanel',
      'colorScheme',
    ]),
    showInEditMode() {
      return !this.isBackchannel
    },
    hasWords() {
      return !!(this.body || this.subject)
    },
    hasAttachment() {
      const attachment = this.post.attachment
      return !!attachment && isUrl(attachment)
    },
    postCommentIds() {
      return this.wallCommentIdsByPost[this.post.id] || []
    },
    xAttachment() {
      if (this.hasAttachment) return true
      if (this.postConfiguration === PostConfiguration.BodyOrAttachment) return !this.hasWords
      return this.showInEditMode
    },
    xWords() {
      if (this.hasWords) return true
      switch (this.postConfiguration) {
        case PostConfiguration.BodyOrAttachment:
          return !this.hasAttachment
        case PostConfiguration.All:
        case PostConfiguration.BodyAndOrAttachment:
        default:
          return true
      }
    },
    xReactions() {
      return this.isReactable
    },
    xComments() {
      return this.isPublished && (this.isCommentable || this.postCommentIds.length > 0)
    },
    xModeration() {
      return !this.isPublished
    },
    isWishContentScrollable() {
      return this.isTimeline
    },
    attachmentLuminance() {
      if (!this.post.attachment) return null
      const attributes = this.linkDisplayAttributesForUrl(this.post.attachment)
      return (attributes && attributes.image_luminance) || null
    },
    wasWrittenByCurrentUser() {
      return !!this.user.id && this.post.author_id === this.user.id
    },
    // Either the current user is a moderator or current user is the author and wall is writable
    isEditableByCurrentUser() {
      return this.canIModerate || (this.wasWrittenByCurrentUser && this.canIWrite)
    },
    rootClasses() {
      return {
        'wish-frame': true,
        'full-height': this.fitHeightToContainer,
      }
    },
  },
  methods: {
    ...mapActions('post', ['changePostText', 'stopEditingPost', 'deletePost', 'approvePost']),
    onOutsideClick() {
      this.stopEditingPost()
    },
    onEscape() {
      this.stopEditingPost()
    },
    confirmDelete() {
      if (
        !this.post.id /* post is new */ ||
        (!this.post.body && !this.post.subject && !this.post.attachment) ||
        confirm(this.__('Are you sure you want to delete this post? This cannot be undone!'))
      ) {
        this.deletePost({ post: this.post })
        this.stopEditingPost()
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.stalker-bar {
  html[data-layout='map'] &,
  html[data-layout='free'] &,
  html[data-layout='timeline'] & {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    background-color: rgba(black, 0.04);
    .surface-post-reaction {
      background-color: transparent !important;
    }
    .surface-post-comments {
      background-color: transparent !important;
    }
  }
  html > [data-color-scheme='dark'] & {
    background-color: rgba(black, 0.06);
    .surface-post-reaction {
      background-color: transparent !important;
    }
    .surface-post-comments {
      background-color: transparent !important;
    }
  }
}

.full-height {
  height: 100%;

  .real-wish {
    height: 100%;
  }
}
.wish-content {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  overflow: auto;
  .surface-post-attachment {
    overflow: visible;
  }
  .words {
    overflow: visible;
  }
}
.vertically-centered {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
}
</style>
